/**
 * Retry Logic with Exponential Backoff
 *
 * Provides utilities for retrying failed operations with exponential backoff and jitter.
 *
 * Features:
 * - Exponential backoff with configurable multiplier
 * - Jitter to prevent thundering herd
 * - Configurable max attempts and delays
 * - Smart retry logic for retryable errors
 *
 * Requirements: 5.2, 9.3
 */

import { AppError, ErrorCategory } from '../../errors/types'
import { logger } from '../../logging/Logger'

/**
 * Retry configuration
 */
export interface RetryConfig {
    /** Maximum number of retry attempts (default: 3) */
    maxAttempts: number

    /** Initial delay in milliseconds (default: 1000) */
    initialDelay: number

    /** Maximum delay in milliseconds (default: 30000) */
    maxDelay: number

    /** Backoff multiplier (default: 2) */
    backoffMultiplier: number

    /** Enable jitter to prevent thundering herd (default: true) */
    jitter: boolean

    /** Custom function to determine if error is retryable */
    shouldRetry?: (error: Error) => boolean

    /** Callback invoked before each retry attempt */
    onRetry?: (attempt: number, delay: number, error: Error) => void
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxAttempts: 3,
    initialDelay: 1000, // 1 second
    maxDelay: 30000, // 30 seconds
    backoffMultiplier: 2,
    jitter: true,
}

/**
 * Determine if an error is retryable
 *
 * Retryable errors include:
 * - Network errors
 * - Server errors (5xx)
 * - Timeout errors
 * - Rate limit errors (429)
 *
 * Non-retryable errors include:
 * - Client errors (4xx except 408, 429)
 * - Authentication errors (401)
 * - Authorization errors (403)
 * - Validation errors
 */
export function shouldRetry(error: Error): boolean {
    // If it's an AppError, check its properties
    if (error instanceof AppError) {
        // Use the recoverable flag if available
        if (error.recoverable !== undefined) {
            return error.recoverable
        }

        // Check error category
        switch (error.category) {
            case ErrorCategory.NETWORK:
            case ErrorCategory.SERVER:
                return true

            case ErrorCategory.AUTHENTICATION:
            case ErrorCategory.AUTHORIZATION:
            case ErrorCategory.VALIDATION:
                return false

            case ErrorCategory.CLIENT:
                // Check if it's a retryable client error (timeout or rate limit)
                if (
                    error.code === 'HTTP_408' ||
                    error.code === 'HTTP_429' ||
                    error.code === 'REQUEST_TIMEOUT'
                ) {
                    return true
                }
                return false

            default:
                // Unknown category - don't retry by default
                return false
        }
    }

    // For generic errors, check the error name/message
    const errorMessage = error.message.toLowerCase()
    const errorName = error.name.toLowerCase()

    // Network-related errors are retryable
    if (
        errorName.includes('network') ||
        errorName.includes('timeout') ||
        errorMessage.includes('network') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('fetch') ||
        errorMessage.includes('econnrefused') ||
        errorMessage.includes('enotfound')
    ) {
        return true
    }

    // Default to not retrying unknown errors
    return false
}

/**
 * Calculate delay for next retry attempt with exponential backoff
 *
 * Formula: min(initialDelay * (backoffMultiplier ^ attempt), maxDelay)
 * With jitter: delay * (0.5 + random(0, 0.5))
 *
 * @param attempt - Current attempt number (0-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
export function calculateDelay(attempt: number, config: RetryConfig): number {
    // Calculate exponential backoff
    const exponentialDelay =
        config.initialDelay * Math.pow(config.backoffMultiplier, attempt)

    // Cap at max delay
    let delay = Math.min(exponentialDelay, config.maxDelay)

    // Add jitter if enabled
    if (config.jitter) {
        // Jitter: multiply by random value between 0.5 and 1.0
        // This prevents all clients from retrying at the same time
        const jitterFactor = 0.5 + Math.random() * 0.5
        delay = delay * jitterFactor
    }

    return Math.floor(delay)
}

/**
 * Sleep for specified duration
 *
 * @param ms - Duration in milliseconds
 */
function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retry a function with exponential backoff
 *
 * @param fn - Async function to retry
 * @param config - Retry configuration (optional)
 * @returns Promise that resolves with the function result or rejects with the last error
 *
 * @example
 * ```typescript
 * const result = await withRetry(
 *   async () => apiClient.get('/users'),
 *   { maxAttempts: 3, initialDelay: 1000 }
 * );
 * ```
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    config: Partial<RetryConfig> = {}
): Promise<T> {
    // Merge with default config
    const fullConfig: RetryConfig = {
        ...DEFAULT_RETRY_CONFIG,
        ...config,
    }

    let lastError: Error

    // Attempt the operation up to maxAttempts times
    for (let attempt = 0; attempt < fullConfig.maxAttempts; attempt++) {
        try {
            // Try to execute the function
            const result = await fn()

            // Success! Return the result
            if (attempt > 0) {
                logger.info('Retry succeeded', {
                    action: 'retry_success',
                    metadata: {
                        attempt: attempt + 1,
                        totalAttempts: fullConfig.maxAttempts,
                    },
                })
            }

            return result
        } catch (error) {
            // Store the error
            lastError =
                error instanceof Error ? error : new Error(String(error))

            // Check if we should retry
            const shouldRetryError = fullConfig.shouldRetry
                ? fullConfig.shouldRetry(lastError)
                : shouldRetry(lastError)

            // If this is the last attempt or error is not retryable, throw
            if (attempt === fullConfig.maxAttempts - 1 || !shouldRetryError) {
                logger.warn(
                    'Retry failed - max attempts reached or error not retryable',
                    {
                        action: 'retry_failed',
                        metadata: {
                            attempt: attempt + 1,
                            totalAttempts: fullConfig.maxAttempts,
                            retryable: shouldRetryError,
                            error: lastError.message,
                        },
                    }
                )
                throw lastError
            }

            // Calculate delay for next attempt
            const delay = calculateDelay(attempt, fullConfig)

            // Log retry attempt
            logger.debug('Retrying after error', {
                action: 'retry_attempt',
                metadata: {
                    attempt: attempt + 1,
                    totalAttempts: fullConfig.maxAttempts,
                    delay,
                    error: lastError.message,
                },
            })

            // Call onRetry callback if provided
            if (fullConfig.onRetry) {
                fullConfig.onRetry(attempt + 1, delay, lastError)
            }

            // Wait before retrying
            await sleep(delay)
        }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError!
}

/**
 * Create a retry wrapper for a function
 *
 * Returns a new function that automatically retries on failure.
 *
 * @param fn - Function to wrap with retry logic
 * @param config - Retry configuration
 * @returns Wrapped function with retry logic
 *
 * @example
 * ```typescript
 * const fetchWithRetry = createRetryWrapper(
 *   (url: string) => fetch(url),
 *   { maxAttempts: 3 }
 * );
 *
 * const response = await fetchWithRetry('/api/users');
 * ```
 */
export function createRetryWrapper<TArgs extends unknown[], TReturn>(
    fn: (...args: TArgs) => Promise<TReturn>,
    config: Partial<RetryConfig> = {}
): (...args: TArgs) => Promise<TReturn> {
    return (...args: TArgs) => withRetry(() => fn(...args), config)
}
